import React, { useState, useEffect, useMemo } from "react";
import { getContext } from "../../api/getContext";
import { getPortalBaseURL } from "../../constants";

export const TABS = ["Plan Benefits", "Why INLIC", "Downloads"];

const TABS_CONTENT_NAMES = {
  "Plan Benefits": {
    structureName: "Journey Plan Benefits Structure",
    webContentName: "Term Journey Plan Benefits",
  },
  "Why INLIC": {
    structureName: "Journey Why INLIC Structure",
    webContentName: "Journey Why INLIC",
  },
  "Downloads": {
    structureName: "Journey Downloads Structure",
    webContentName: "Term Journey Downloads",
  },
};

const getTabContentNames = (tab) =>
  TABS_CONTENT_NAMES[tab] ?? {
    structureName: tab,
    webContentName: tab,
};

const normalizePlanBenefits = (data) => {
  if (!data?.planBenefitsGroup?.length) return [];
  return data.planBenefitsGroup.map((item, index) => ({
    id: index,
    list: item.planBenefit,
  }));
};

const normalizeWhyInlic = (data) => {
  if (!data?.journeyWhyInlic?.length) return [];
  const baseUrl = getPortalBaseURL();
  return data.journeyWhyInlic.map((item, index) => ({
    id: item.icon?.fileEntryId ?? index,
    title: item.value,
    subtitle: item.title,
    description: item.effectiveTime,
    image: item.icon?.url ? `${baseUrl}${item.icon.url}` : null,
  }));
};

// Liferay returns a preview URL for files it can rasterize, which serves a
// single page image instead of the uploaded file.
const toFileURL = (url) => {
  if (!url) return "";
  const [path, query] = url.split("?");
  if (!query) return url;
  const params = query
    .split("&")
    .filter((param) => !param.startsWith("previewFileIndex="));
  return params.length ? `${path}?${params.join("&")}` : path;
};

const normalizeDownloads = (data) => {
  if (!data?.titleGroup?.length) return [];
  return data.titleGroup.map((group, index) => ({
    id: index,
    heading: group.title,
    items: (group.documentsGroup ?? []).map((doc, docIndex) => ({
      id: doc.uploadDocument?.fileEntryId ?? docIndex,
      title: doc.documentTitle,
      url: toFileURL(doc.uploadDocument?.url),
      downloadIcon: true,
    })),
  }));
};

export const normalizeContextData = (tab, data) => {
  if (!data) return [];
  switch (tab) {
    case "Plan Benefits":
      return normalizePlanBenefits(data);
    case "Why INLIC":
      return normalizeWhyInlic(data);
    case "Downloads":
      return normalizeDownloads(data);
    default:
      return [];
  }
};

const preloadAllTabData = async () => {
  const entries = await Promise.all(
    TABS.map(async (tab) => {
      const { structureName, webContentName } = getTabContentNames(tab);
      try {
        const response = await getContext(structureName, webContentName);
        return [tab, normalizeContextData(tab, response)];
      } catch {
        return [tab, []];
      }
    }),
  );
  return Object.fromEntries(entries);
};

const QuickPeep = () => {
  const [activeTab, setActiveTab] = useState("Why INLIC");
  const [tabDataByTab, setTabDataByTab] = useState(() =>
    Object.fromEntries(TABS.map((tab) => [tab, []])),
  );

  useEffect(() => {
    let cancelled = false;

    preloadAllTabData().then((nextTabData) => {
      if (!cancelled) {
        setTabDataByTab(nextTabData);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const activeTabData = useMemo(
    () => tabDataByTab[activeTab] ?? [],
    [activeTab, tabDataByTab],
  );

  return (
    <div className="premium-summary-section quickPeep-section">
      <div className="quickPeep-section__tab-wrraper">
        <h6 className="quickPeep-title">Take a Quick Peep</h6>
        <div className="tabs-wrraper">
          {TABS.map((tab) => (
            <button
              className={`tabs ${activeTab === tab ? "active" : ""}`}
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>
      <div className="quickPeep-body">
        {activeTabData.length > 0 &&
          activeTabData.map((item) => (
            <div className="quickPeep-body-item" key={item.id}>
              {activeTab === "Plan Benefits" && (
                <div className="quickPeep-body-list">
                  {item.list && <span>{item.list}</span>}
                </div>
              )}

              {activeTab === "Why INLIC" && (
                <>
                  {item.image && (
                    <img src={item.image} width="80" height="80" alt="" />
                  )}
                  <div className="quickPeep-body-disc">
                    {item.title && <h5 className="bold">{item.title}</h5>}

                    {item.subtitle && (
                      <p className="small-body medium">{item.subtitle}</p>
                    )}

                    {item.description && (
                      <span className="caption-text light">
                        {item.description}
                      </span>
                    )}
                  </div>
                </>
              )}

              {activeTab === "Downloads" && (
                <div className="quickPeep-body-disc">
                  <h5 className="bold">{item.heading}</h5>
                  <ul className="downloads-item-list">
                    {item.items.map((download) => (
                      <li key={download.id}>
                        <a
                          href={`${getPortalBaseURL()}${download.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {download.title}
                          {download.downloadIcon && (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 21 21"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M19.0288 19.0286L1.02881 19.0286M16.0288 9.02856L10.0288 15.0286M10.0288 15.0286L4.02881 9.02856M10.0288 15.0286L10.0288 1.02856"
                                stroke="#96272A"
                                strokeWidth="2.05714"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export default QuickPeep;
